import { motion } from "framer-motion";
import { 
  Rocket, Calendar, Eye, Phone, ChefHat, Mic, 
  TrendingUp, Globe, Smartphone, Brain, Shield,
  CheckCircle2, Clock, Sparkles, Crown, MessageSquare,
  CreditCard, Users, Camera
} from "lucide-react";

const roadmapItems = [
  {
    quarter: "Q1 2026",
    title: "Foundation Launch",
    status: "live",
    features: [
      { name: "Supervisor Echo — AI Phone Receptionist", icon: Phone, done: true },
      { name: "Supervisor Apollo — Vision AI (Image Upload)", icon: Eye, done: true },
      { name: "Supervisor Athena — Voice SOP Builder", icon: ChefHat, done: true },
      { name: "Supervisor Atlas — Staff Coordination (in development)", icon: Users, done: false },
      { name: "Supervisor Iris — Kitchen Traffic Control (in development)", icon: MessageSquare, done: false },
    ]
  },
  {
    quarter: "Q3 2026",
    title: "Integration Layer",
    status: "next",
    features: [
      { name: "POS Integration (Toast, Square, Clover, TouchBistro)", icon: CreditCard, done: false },
      { name: "KDS (Kitchen Display System) Integration", icon: TrendingUp, done: false },
      { name: "Smart SMS Notifications for Owners", icon: Phone, done: false },
      { name: "Expo Multi-Device Support (Watch, Earpiece, Phone)", icon: Smartphone, done: false },
    ]
  },
  {
    quarter: "Q4 2026",
    title: "Vision & Intelligence",
    status: "planned",
    features: [
      { name: "Live RTSP Camera Integration (Apollo Continuous)", icon: Camera, done: false },
      { name: "Real-Time Plating Quality Detection", icon: Eye, done: false },
      { name: "Customer Sentiment Analytics", icon: Brain, done: false },
      { name: "Multi-Location Dashboard", icon: Globe, done: false },
    ]
  },
  {
    quarter: "2027+",
    title: "Mobile & Scale",
    status: "planned",
    features: [
      { name: "iOS & Android Owner App", icon: Smartphone, done: false },
      { name: "iOS & Android Staff App", icon: Smartphone, done: false },
      { name: "Menu Engineering Dashboard", icon: Brain, done: false },
      { name: "Enterprise Multi-Brand Support", icon: Globe, done: false },
    ]
  },
];

const getStatusColor = (status) => {
  switch (status) {
    case "live": return { bg: "bg-lime/10", border: "border-lime/40", text: "text-lime", badge: "LIVE NOW" };
    case "next": return { bg: "bg-gold/10", border: "border-gold/40", text: "text-gold", badge: "UP NEXT" };
    default: return { bg: "bg-slate-700/30", border: "border-slate-600", text: "text-slate-400", badge: "PLANNED" };
  }
};

export const RoadmapSection = ({ remaining }) => {
  return (
    <section data-testid="roadmap-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
        >
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-gold">
            Year One Roadmap
          </span>
          <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4">
            Why <span className="text-gold">Founding Members</span> Win
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-2xl mx-auto">
            Every feature we ship increases your ROI. Founders lock in $374/month today and get everything we build — forever.
          </p>
        </motion.div>

        {/* Price Comparison */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Founder Price */}
          <div className="glass-card rounded-xl p-6 border-2 border-gold gold-glow relative overflow-hidden">
            <div className="absolute -top-1 -right-1 bg-gold text-indigo-700 text-xs font-bold px-3 py-1 rounded-bl">
              <Crown className="inline w-3 h-3 mr-1" />
              FOUNDING 50
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-inter font-black text-gold">$374</span>
              <span className="text-slate-400">/month</span>
            </div>
            <p className="text-sm text-lime font-medium mb-3">Locked forever. Never increases.</p>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime" /> All current features
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime" /> All future features included
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime" /> Direct founder access
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime" /> Priority support forever
              </li>
            </ul>
            <p className="mt-4 text-xs text-slate-500">
              {remaining !== undefined && remaining > 0 ? (
                <span className="text-gold font-bold">{remaining} spots remaining</span>
              ) : (
                <span className="text-red-400">Sold out</span>
              )}
            </p>
          </div>

          {/* Standard Price */}
          <div className="glass-card rounded-xl p-6 border border-slate-600 relative">
            <div className="absolute -top-1 -right-1 bg-slate-700 text-slate-300 text-xs font-bold px-3 py-1 rounded-bl">
              STANDARD
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-inter font-black text-slate-300">$499</span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="text-sm text-slate-500 mb-3">After founding spots fill</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-slate-600" /> All current features
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-slate-600" /> Future features as add-ons
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" /> Standard support queue
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" /> No price lock guarantee
              </li>
            </ul>
            <p className="mt-4 text-xs text-slate-600">
              Price may increase as features grow
            </p>
          </div>
        </motion.div>

        {/* Roadmap Timeline */}
        <div className="space-y-8">
          {roadmapItems.map((quarter, idx) => {
            const status = getStatusColor(quarter.status);
            return (
              <motion.div
                key={quarter.quarter}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`${status.bg} rounded-xl border ${status.border} p-6 md:p-8`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg ${status.bg} border ${status.border} flex items-center justify-center`}>
                      {quarter.status === "live" ? (
                        <Rocket className={`w-5 h-5 ${status.text}`} />
                      ) : (
                        <Calendar className={`w-5 h-5 ${status.text}`} />
                      )}
                    </div>
                    <div>
                      <span className={`text-xs font-bold tracking-wider ${status.text}`}>{status.badge}</span>
                      <h3 className="font-inter text-xl font-bold text-white">{quarter.quarter}: {quarter.title}</h3>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {quarter.features.map((feature, i) => {
                    const Icon = feature.icon;
                    return (
                      <div 
                        key={i} 
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          feature.done ? "bg-lime/5 border border-lime/20" : "bg-slate-800/30 border border-slate-700/30"
                        }`}
                      >
                        {feature.done ? (
                          <CheckCircle2 className="w-5 h-5 text-lime shrink-0" />
                        ) : (
                          <Icon className="w-5 h-5 text-slate-500 shrink-0" />
                        )}
                        <span className={`text-sm ${feature.done ? "text-white" : "text-slate-400"}`}>
                          {feature.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-slate-400 mb-4">
            <Sparkles className="inline w-4 h-4 text-gold mr-2" />
            Founders get <span className="text-gold font-bold">16+ features</span> for one locked price
          </p>
          <p className="text-sm text-slate-500">
            Talk to AI Argus to see your personalized savings and start your free trial
          </p>
        </motion.div>
      </div>
    </section>
  );
};
